import React from 'react';
import { Activity, Globe, Smartphone, Monitor, Tablet } from 'lucide-react';

export default function LiveFeedCard({ stream = [] }) {
  const getDeviceIcon = (dev) => {
    if (dev === 'mobile') return Smartphone;
    if (dev === 'tablet') return Tablet;
    return Monitor;
  };

  const getEventBadgeClass = (ev) => {
    if (ev === 'pageview') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    if (ev.includes('dwell')) return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    if (ev.includes('print') || ev.includes('download')) return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300';
    if (ev.includes('affiliate') || ev.includes('outbound')) return 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300';
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300';
  };

  const formatRelativeTime = (ts) => {
    if (!ts) return '';
    try {
      const diffMs = Date.now() - new Date(ts).getTime();
      const diffSec = Math.max(0, Math.floor(diffMs / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      return `${Math.floor(diffHr / 24)}d ago`;
    } catch {
      return ts;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-emerald-500 animate-pulse" />
            Live Event Stream
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Real-time chronological telemetry feed
          </p>
        </div>
        <span className="text-[10px] font-mono text-slate-400">
          Latest {stream.length}
        </span>
      </div>

      {stream.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">
          No live events in this timeframe.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
          {stream.map((item, idx) => {
            const DevIcon = getDeviceIcon(item.device);
            const badgeClass = getEventBadgeClass(item.event_type || '');

            return (
              <div key={idx} className="py-2.5 flex items-start justify-between gap-3 text-xs">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold font-mono ${badgeClass}`}>
                      {item.event_type || 'event'}
                    </span>
                    {item.site && (
                      <span className="px-1 py-0.2 rounded text-[9px] font-mono bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        {item.site}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {item.country || 'XX'}
                    </span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <DevIcon className="w-3 h-3" />
                      {item.device || 'desktop'}
                    </span>
                  </div>

                  <div className="text-[11px] text-slate-700 dark:text-slate-300 font-mono truncate">
                    {item.path || '/'}
                  </div>

                  {item.target && (
                    <div className="text-[10px] text-amber-600 dark:text-amber-400 font-mono truncate">
                      ➔ {item.target}
                    </div>
                  )}
                </div>

                <div className="text-right flex-shrink-0 text-[10px] font-mono text-slate-400 whitespace-nowrap">
                  {formatRelativeTime(item.timestamp)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
